/**
 * TanStack Query hooks каталога сортов + очереди разбора сортов.
 *
 * Мутации инвалидируют корневой ключ `['varieties']` целиком: разбор очереди
 * создаёт сорта и алиасы, то есть меняет и каталог, и очередь одновременно.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Variety,
  VarietyCreatePayload,
  VarietyListQuery,
  VarietyListResponse,
  VarietyPatchPayload,
  VarietyQueueItem,
  VarietyQueueList,
} from './types';

const BASE = '/api/v1/reference/varieties';

export const varietyKeys = {
  all: ['varieties'] as const,
  list: (q: VarietyListQuery) => ['varieties', 'list', q] as const,
  queue: (q: Record<string, unknown>) => ['varieties', 'queue', q] as const,
};

function buildParams(q: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: varietyKeys.all });
}

export function useVarieties(query: VarietyListQuery) {
  return useQuery({
    queryKey: varietyKeys.list(query),
    queryFn: () =>
      api.get<VarietyListResponse>(`${BASE}${buildParams({ ...query })}`),
    staleTime: 30_000,
  });
}

export function useCreateVariety() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (payload: VarietyCreatePayload) =>
      api.post<Variety>(BASE, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateVariety() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: VarietyPatchPayload }) =>
      api.patch<Variety>(`${BASE}/${id}`, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteVariety() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`${BASE}/${id}`),
    onSuccess: invalidate,
  });
}

// ─── очередь разбора сортов ───────────────────────────────────────────────

export function useVarietyQueue(query: {
  status?: string;
  crop_id?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: varietyKeys.queue(query),
    queryFn: () =>
      api.get<VarietyQueueList>(`${BASE}/queue/items${buildParams({ ...query })}`),
    staleTime: 15_000,
  });
}

export function useMapVarietyQueueItem() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, varietyId }: { id: string; varietyId: string }) =>
      api.post<VarietyQueueItem>(`${BASE}/queue/${id}/map`, {
        variety_id: varietyId,
        create_alias: true,
      }),
    onSuccess: invalidate,
  });
}

export function useCreateVarietyFromQueue() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: { crop_id?: string; name_ru?: string; is_hybrid?: boolean };
    }) => api.post<VarietyQueueItem>(`${BASE}/queue/${id}/create`, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteVarietyQueueItem() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`${BASE}/queue/${id}`),
    onSuccess: invalidate,
  });
}
